class Utils {
  /**
   * Returns the first element that is a descendant of node that
   * matches selectors.
   *
   * @param {string} selector - A DOMString containing one or more selectors to match. This string
   *   must be a valid CSS selector string
   * @param {Object} [config] - You can pass a context to scope the element selection
   * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element
   *   in
   *
   * @returns {Element|null} The found element or null
   */
  static getElement (selector, { context = document } = {}) {
    return context.querySelector(selector)
  }

  /**
   * Returns a list of elements that are a descendant of node that
   * matches selectors.
   *
   * @param {string} selector - A DOMString containing one or more selectors to match. This string
   *   must be a valid CSS selector string
   * @param {Object} [config] - You can pass a context to scope the element selection
   * @param {HTMLElement|Document} [context=Document] - The scope we will try and find the element
   *   in
   * @param {boolean} [asArray=true] - Return the elements as an Array instead of an NodeList?
   *
   * @returns {Array<Element>|NodeListOf<Element>} The found elements or null
   */
  static getElements (selector, { context = document, asArray = true } = {}) {
    const items = context.querySelectorAll(selector)

    return asArray ? Array.from(items) : items
  }

  /**
   * Checks if the passed string contains any items in an array
   *
   * @param {string} str - The string we will be searching
   * @param {Array} [items] - A list of the items we will be checking for presence in the str
   *
   * @returns {boolean}
   */
  static doesStringContainAny (str, items = []) {
    return items.some((i) => str.includes(i))
  }

  /**
   * Creates an HTMLElement
   *
   * @param {string} tag - What type of element we want this to be
   * @param {Object} [options] - A key / value pair of properties and values we want to set on the
   *   newly created element
   *
   * @returns {HTMLElement}
   */
  static createElement (tag, options = {}) {
    const el = document.createElement(tag)

    Object.entries(options).forEach(([key, value]) => {
      el.setAttribute(key, value)
    })

    return el
  }

  /**
   * Creates an array starting from zero with the length of the passed amount
   *
   * @param {number} amount - How long do we want this array to be?
   *
   * @returns {Array}
   */
  static range (amount) {
    return [...Array(amount).keys()]
  }

  /**
   * camelCase a string
   *
   * @param {string} str
   *
   * @return {string} value - formatted value
   */
  static camelize (str) {
    return str
      .replace(/(?:^\w|[A-Z]|\b\w)/g, (word, index) => {
        return index === 0 ? word.toLowerCase() : word.toUpperCase()
      })
      .replace(/\s+/g, '')
  }

  /**
   * A Javascript equivalent of Shopify's | img_url filter
   *
   * @param {Object} The properties we will use to create the URL
   * @param {string} src - The Shopify CDN URL of the image
   * @param {string} size - What size we want the image to be
   * @param {null|String} [crop=null] - By default the images aren't cropped but we can choose to
   *   crop them
   * @param {null|Number} [scale=null] - Images are 1x by default, you can choose to pass a
   *   different scale
   *
   * @returns {string}
   */
  static resizeImage ({ src, size, crop = null, scale = null } = {}) {
    if (size == null) {
      return src
    }

    if (size === 'master') {
      return src.replace(/http(s)?:/, '')
    }

    const match = src.match(
      /\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?v=\d+)?/i,
    )

    if (match != null) {
      const prefix = src.split(match[0])
      const suffix = match[0]
      const scaleString = scale ? `@${scale}x` : ''
      const cropString = crop ? `_crop_${crop}` : ''

      return `${prefix[0]}_${size}${cropString}${scaleString}${suffix}`.replace(
        /http(s)?:/,
        '',
      )
    } else {
      return null
    }
  }

  /**
   * This is the default config we will use when using fetch()
   *
   * @param {string} type
   *
   * @returns {Object}
   */
  static getDefaultRequestConfig (type = 'json') {
    return {
      method: 'POST',
      headers: {
        Accept: `application/${type}`,
        'Content-Type': 'application/json',
      },
    }
  }

  /**
   * Default fetch boilerplate
   * @param {string} url - The URL we are looking to fetch
   * @param {Object} [config] - .fetch config to override the default
   *
   * @returns {Promise}
   */
  static fetchJSON (url, config = {}) {
    return fetch(
      url,
      Object.assign({}, Utils.getDefaultRequestConfig(), config),
    ).then((response) => {
      if (!response.ok) {
        throw response
      }
      return response.json()
    })
  }

  /**
   * This will fetch product data from Shopify's AJAX API by handle
   *
   * @param {string} handle - The products handle
   *
   * @returns {Promise}
   */
  static fetchProductData (handle) {
    try {
      return Utils.fetchJSON(`/products/${handle}.js`)
    } catch (e) {
      console.error(e)
    }
  }

  /**
   * Gets the offsetTop of the passed element relevant to it's offsetParent
   *
   * @param {HTMLElement|Element} el - The element we'll be getting the offsetTop for
   * @param {Document|HTMLElement} [parent=HTMLBodyElement] - The offsetParent we want to get the
   *   offsetTop relative to
   * @param {number} [offsetTop=0] - The starting offsetTop. This tends to be 0 but given this is a
   *   recursive function, we need to pass it to let it build up
   *
   * @returns {number}
   */
  static getOffsetTop (el, parent = document.body, offsetTop = 0) {
    if (!isNaN(el.offsetTop)) {
      offsetTop += el.offsetTop
    }

    return el.offsetParent === parent
      ? offsetTop
      : Utils.getOffsetTop(el.offsetParent, parent, offsetTop)
  }

  /**
   * Format money values based on your shop currency settings
   * @param  {number|string} cents - value in cents or dollar amount e.g. 300 cents
   * or 3.00 dollars
   * @param  {string} [format] - shop money_format setting
   *
   * @return {string} value - formatted value
   */
  static formatMoney (cents, format = window.theme.moneyFormat) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '')
    }
    let value = ''
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
    const formatString = format

    function formatWithDelimiters (
      number,
      precision = 2,
      thousands = ',',
      decimal = '.',
    ) {
      if (isNaN(number) || number == null) {
        return 0
      }

      number = (number / 100.0).toFixed(precision)

      const parts = number.split('.')
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        `$1${thousands}`,
      )
      const centsAmount = parts[1] ? decimal + parts[1] : ''

      return dollarsAmount + centsAmount
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2)
        break
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0)
        break
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',')
        break
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',')
        break
    }

    return formatString.replace(placeholderRegex, value)
  }

  static getOrdinalForDay (day) {
    if (day > 3 && day < 21) return 'th'
    switch (day % 10) {
      case 1:
        return 'st'
      case 2:
        return 'nd'
      case 3:
        return 'rd'
      default:
        return 'th'
    }
  }

  /**
   * Inject variables into a translated string.
   *
   * @param {string} translation
   * @param {{ [variable: string]: any }} variables
   * @param {{ delimiters?: [string, string][] }} [options]
   *
   * @example
   * // returns Hurry! Only 2 left in stock.
   * translateString('Hurry! Only {{ count }} left in stock.', { count: 2 })
   */
  static translateString (
    translation,
    variables,
    options = {
      delimiters: [
        ['{{\\s*', '\\s*}}'],
        ['%{', '}'],
      ],
    },
  ) {
    return Object.entries(variables).reduce((baseTranslation, [key, value]) => {
      const replacePattern = options.delimiters
        .map(([start, end]) => `${start}${key}${end}`)
        .join('|')

      return baseTranslation.replace(new RegExp(replacePattern, 'g'), value)
    }, translation)
  }

  /**
   * @param {Function} fn
   * @param {number} wait
   *
   * @return {Function}
   */
  static debounce (fn, wait) {
    let t

    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  /**
   * @param {FormData} formData
   *
   * @return {Object}
   */
  static buildProperties (formData) {
    const propertyKeyValues = [...formData.entries()].filter(
      ([key, value]) => key.includes('properties[') && value !== '',
    )

    /**
     * @param {Object} properties
     * @param {[string, string]} property
     *
     * @returns {Object}
     */
    function propertyReducer (properties, [key, value]) {
      const strippedKey = key.replace('properties[', '').slice(0, -1)

      return {
        ...properties,
        [strippedKey]: value,
      }
    }

    return propertyKeyValues.reduce(
      (properties, property) => propertyReducer(properties, property),
      {},
    )
  }

  /**
   * Returns the current viewport size.
   * @return {'xs'|'sm'|'md'|'lg'|'xl'}
   */
  static viewportSize () {
    const breakpoints = {
      xs: window.matchMedia('(max-width: 767px)').matches,
      sm: window.matchMedia('(min-width: 768px) and (max-width: 900px)')
        .matches,
      md: window.matchMedia('(min-width: 901px) and (max-width: 1200px)')
        .matches,
      lg: window.matchMedia('(min-width: 1201px) and (max-width: 1439px)')
        .matches,
      xl: window.matchMedia('(min-width: 1440px)').matches,
    }
    return Object.keys(breakpoints).find((key) => breakpoints[key])
  }

  /**
   * @callback PreReplaceCallback
   * @param {Element} node
   */

  /**
   * @callback PostReplaceCallback
   * @param {Element} node
   */

  /**
   * @param {Element} oldNode
   * @param {Element} newContent
   * @param {PreReplaceCallback[]} preReplaceCallbacks
   * @param {PostReplaceCallback[]} postReplaceCallbacks
   */
  static gracefullyReplaceNode (
    oldNode,
    newContent,
    preReplaceCallbacks = [],
    postReplaceCallbacks = [],
  ) {
    for (const preReplaceCallback of preReplaceCallbacks) {
      preReplaceCallback(newContent)
    }

    const newNodeWrapper = Utils.createElement('div')
    setInnerHTML(newNodeWrapper, newContent.outerHTML)
    const newNode = newNodeWrapper.firstChild

    stopIdsConflicting()

    oldNode.parentNode.insertBefore(newNode, oldNode)
    oldNode.style.display = 'none'

    for (const postReplaceCallback of postReplaceCallbacks) {
      postReplaceCallback(newNode)
    }

    setTimeout(() => oldNode.remove(), 500)

    /**
     * @param {Element} element
     * @param {string} html
     *
     * Sets inner HTML and re-injects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
     */
    function setInnerHTML (element, html) {
      element.innerHTML = html

      for (const oldScriptElement of element.querySelectorAll('script')) {
        const newScriptElement = Utils.createElement('script')

        for (const attribute of Array.from(oldScriptElement.attributes)) {
          newScriptElement.setAttribute(attribute.name, attribute.value)
        }

        newScriptElement.appendChild(
          document.createTextNode(oldScriptElement.innerHTML),
        )
        oldScriptElement.parentNode.replaceChild(
          newScriptElement,
          oldScriptElement,
        )
      }
    }

    function stopIdsConflicting () {
      const uniqueKey = Date.now()

      for (const oldElement of oldNode.querySelectorAll('[id], [form]')) {
        oldElement.id && (oldElement.id = `${oldElement.id}-${uniqueKey}`)
        oldElement.form &&
          oldElement.setAttribute(
            'form',
            `${oldElement.form.getAttribute('id')}-${uniqueKey}`,
          )
      }
    }
  }
}

window.BAO.Utils = Utils